import { cn } from "@/lib/utils.ts";
import React from "react";

/**
 * Brand logo, just the icon.
 */
export const OkthxLogo = ({ ref, className, ...props }: React.ComponentPropsWithRef<"svg">) => (
  <svg
    ref={ref}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="-0.322 0.247 24 24"
    fill="none"
    {...props}
    className={cn(className)}
  >
    <title>Okthx Logo</title>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.6784 0.568481C5.22858 0.568482 3.22309e-07 5.79706 0 12.2468C0 18.6966 5.22858 23.9252 11.6784 23.9252C18.1281 23.9252 23.3567 18.6966 23.3567 12.2468C23.3567 5.79706 18.1281 0.568482 11.6784 0.568481ZM16.4392 6.5711C16.8579 6.09271 17.6265 6.02611 18.0823 6.53939C19.4251 8.05134 20.24 10.042 20.24 12.2223C20.24 14.3688 19.4501 16.3316 18.1443 17.8347C17.6907 18.3567 16.9152 18.2931 16.4936 17.8115L12.2083 12.9157C11.8297 12.4832 11.8297 11.8372 12.2083 11.4047L16.4392 6.5711ZM10.4909 5.09478C10.4909 4.38892 9.83048 3.79848 9.09447 4.03114C5.62912 5.12659 3.11608 8.36787 3.11608 12.1965C3.11608 16.025 5.62912 19.2663 9.09447 20.3618C9.83048 20.5944 10.4909 20.004 10.4909 19.2981V5.09478Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Brand logo, icon and wordmark. Pass `hideGlyph` for the text-only "okthx"
 * lockup — the circular brand glyph (paths 0–23 on the x axis) is dropped and
 * the viewBox is cropped to the first letter (x≈30.58), so we reuse the same
 * letterforms rather than shipping a second asset.
 */
export const OkThxWordmark = ({
  ref,
  className,
  hideGlyph = false,
  ...props
}: React.ComponentPropsWithRef<"svg"> & { hideGlyph?: boolean }) => {
  return (
    <svg
      ref={ref}
      viewBox={hideGlyph ? "30.584 0 75.416 25" : "0 0 106 25"}
      height="25"
      width={hideGlyph ? "75.416" : "106"}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
      className={className}
    >
      <title>Okthx</title>
      <path
        d="M90.8685 20.8689L96.1561 13.3876L91.0935 6.13123H95.2842L98.4343 10.8H98.4905L101.669 6.13123H105.775L100.684 13.3032L106 20.8689H101.809L98.4343 15.8907H98.378L95.003 20.8689H90.8685Z"
        fill="currentColor"
      />
      <path
        d="M62.9805 6.13147H66.1868V1.99704H69.8431V6.13147H74.3994V9.80224H69.8431L69.8449 15.9502C69.8449 16.3814 69.9386 16.7002 70.1261 16.9065C70.3324 17.094 70.6418 17.1877 71.0543 17.1877H74.4012L74.3994 20.8692H70.2087C68.8587 20.8692 67.8462 20.541 67.1712 19.8848C66.5149 19.2098 66.1868 18.2441 66.1868 16.9879V9.80224H62.9805V6.13147Z"
        fill="currentColor"
      />
      <path
        d="M52.1873 0V20.869H48.531V0H52.1873ZM62.425 6.13134L56.4624 12.7127L62.9875 20.869H58.4593L52.1873 12.8252L58.0374 6.13134H62.425Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M38.3466 17.5502C40.6145 17.5502 42.4529 15.7118 42.4529 13.4439C42.4529 11.1761 40.6145 9.33761 38.3466 9.33761C36.0788 9.33761 34.2403 11.1761 34.2403 13.4439C34.2403 15.7118 36.0788 17.5502 38.3466 17.5502ZM38.3466 21.2065C42.6338 21.2065 46.1092 17.7311 46.1092 13.4439C46.1092 9.15674 42.6338 5.6813 38.3466 5.6813C34.0595 5.6813 30.584 9.15674 30.584 13.4439C30.584 17.7311 34.0595 21.2065 38.3466 21.2065Z"
        fill="currentColor"
      />
      <rect x="76.4284" width="3.6563" height="20.869" fill="currentColor" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M76.4284 12.1188C76.4284 8.53241 79.3246 5.62506 82.8972 5.62506C86.4699 5.62506 89.3661 8.53241 89.3661 12.1188V20.8691H85.7098V12.1188C85.7098 10.5595 84.4506 9.29544 82.8972 9.29544C81.3439 9.29544 80.0847 10.5595 80.0847 12.1188V20.8691H76.4284V12.1188Z"
        fill="currentColor"
      />
      {!hideGlyph && (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.6784 1.56848C5.22858 1.56848 3.22309e-07 6.79706 0 13.2468C0 19.6966 5.22858 24.9252 11.6784 24.9252C18.1281 24.9252 23.3567 19.6966 23.3567 13.2468C23.3567 6.79706 18.1281 1.56848 11.6784 1.56848ZM16.4392 7.5711C16.8579 7.09271 17.6265 7.02611 18.0823 7.53939C19.4251 9.05134 20.24 11.042 20.24 13.2223C20.24 15.3688 19.4501 17.3316 18.1443 18.8347C17.6907 19.3567 16.9152 19.2931 16.4936 18.8115L12.2083 13.9157C11.8297 13.4832 11.8297 12.8372 12.2083 12.4047L16.4392 7.5711ZM10.4909 6.09478C10.4909 5.38892 9.83048 4.79848 9.09447 5.03114C5.62912 6.12659 3.11608 9.36787 3.11608 13.1965C3.11608 17.025 5.62912 20.2663 9.09447 21.3618C9.83048 21.5944 10.4909 21.004 10.4909 20.2981V6.09478Z"
          fill="currentColor"
        />
      )}
    </svg>
  );
};
